import React, { useMemo, useEffect, useRef } from 'react'
import './PreviewPhone.scss'

interface PreviewPhoneProps {
  localId: number
  localSlug: string
  visible: boolean
  reloadKey: number
  onClose?: () => void
  deviceType?: 'desktop' | 'tablet'
  isModal?: boolean
}

const buildPreviewUrl = (localId: number, localSlug: string, cacheBust: number) => {
  const origin = window.location.origin
  // Add pv=1 and cache busters to force reload when needed
  return `${origin}/web/${localSlug}-${localId}?pv=1&_=${cacheBust}`
}

export default function PreviewPhone({ 
  localId, 
  localSlug, 
  visible, 
  reloadKey, 
  onClose,
  deviceType = 'desktop',
  isModal = false 
}: PreviewPhoneProps) {
  const src = useMemo(() => buildPreviewUrl(localId, localSlug, reloadKey), [localId, localSlug, reloadKey])
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Mejorar la experiencia de scroll en desktop
  useEffect(() => {
    if (visible && deviceType === 'desktop' && iframeRef.current) {
      const iframe = iframeRef.current
      
      // Aplicar estilos de scroll personalizados después de cargar
      const handleLoad = () => {
        try {
          // Intentar acceder al contenido del iframe (si es del mismo origen)
          if (iframe.contentDocument) {
            const style = iframe.contentDocument.createElement('style')
            style.textContent = `
              html {
                scroll-behavior: smooth;
              }
              body {
                overflow-x: hidden;
              }
              ::-webkit-scrollbar {
                width: 8px;
              }
              ::-webkit-scrollbar-track {
                background: rgba(0,0,0,0.1);
              }
              ::-webkit-scrollbar-thumb {
                background: rgba(252, 117, 86, 0.3);
                border-radius: 4px;
              }
              ::-webkit-scrollbar-thumb:hover {
                background: rgba(252, 117, 86, 0.5);
              }
            `
            iframe.contentDocument.head.appendChild(style)
          }
        } catch (e) {
          // Si no podemos acceder al contenido (CORS), no hacemos nada
          console.log('Cannot access iframe content due to CORS policy')
        }
      }

      iframe.addEventListener('load', handleLoad)
      
      return () => {
        iframe.removeEventListener('load', handleLoad)
      }
    }
  }, [visible, deviceType, src])

  if (!visible) return null

  const containerClass = `preview-phone-container ${deviceType === 'tablet' ? 'tablet-mode' : 'desktop-mode'} ${isModal ? 'modal-style' : ''}`

  return (
    <div className={containerClass}>
      {isModal && <div className="modal-overlay" onClick={onClose} />}
      <div className="preview-phone-wrapper">
        <div className="preview-phone-header">
          <span>Vista previa</span>
          <div className="spacer"/>
          <button className="btn-link" onClick={onClose}>
            {isModal ? '✕' : 'Cerrar'}
          </button>
        </div>
        <div className="phone-frame">
          {/* Volume buttons */}
          <div className="volume-up" />
          <div className="volume-down" />
          
          {/* Notch with speaker and camera */}
          <div className="phone-notch" />
          
          {/* Screen */}
          <iframe 
            ref={iframeRef}
            key={src} 
            title="phone-preview" 
            className="phone-iframe" 
            src={src}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  )
}
